import { create } from "zustand";

export type ReservationType =
    | "COURT"
    | "ADULT"
    | "CHILD_LESS_THEN_EIGHT"
    | "CHILD_ABOVE_EIGHT"
    | "CHILD_TWELVE_TO_SIXTEEN"
    | "MEMBERSHIP";

export type AdminNotificationEvent =
    | {
    eventType: "NEW_RESERVATION";
    reservationId: number;
    reservationType: ReservationType;
    username: string;
    phoneNumber: string;
}
    | {
    eventType: "CANCEL_REQUEST";
    reservationId: number;
    username: string;
    phoneNumber: string;
}
    | {
    eventType: "BROWSER_ACCESS_PENDING";
    browserClientUserId: number;
    phoneNumber: string;
};

export type AdminNotificationItem = {
    id: string; // unique
    createdAt: number; // ms
    read: boolean;

    // domain
    eventType: AdminNotificationEvent["eventType"];
    reservationId?: number;
    reservationType?: ReservationType; // only for NEW_RESERVATION
    browserClientUserId?: number;

    username?: string;
    phoneNumber?: string;

    // UI
    title: string;
};

type NotificationsStore = {
    items: AdminNotificationItem[];
    unreadCount: number;

    pushEvent: (evt: AdminNotificationEvent) => void;
    markRead: (id: string) => void;
    markAllRead: () => void;
    clear: () => void;
};

const typeLabel: Record<ReservationType, string> = {
    COURT: "Бронь корта",
    ADULT: "Взрослые группы",
    CHILD_LESS_THEN_EIGHT: "Дети 5–7",
    CHILD_ABOVE_EIGHT: "Дети 8–16",
    CHILD_TWELVE_TO_SIXTEEN: "Дети 12–16",
    MEMBERSHIP: "Абонементы",
};

function buildTitle(evt: AdminNotificationEvent): string {
    if (evt.eventType === "NEW_RESERVATION") {
        const label = typeLabel[evt.reservationType] ?? evt.reservationType;
        return `Новая заявка #${evt.reservationId} · ${label}`;
    }
    if (evt.eventType === "BROWSER_ACCESS_PENDING") {
        return `Новая заявка на доступ · ${evt.phoneNumber} · #${evt.browserClientUserId}`;
    }
    return `Запрос на отмену по заявке #${evt.reservationId}`;
}

function uid(): string {
    // достаточно для UI
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export const useNotificationsStore = create<NotificationsStore>((set, get) => ({
    items: [],
    unreadCount: 0,

    pushEvent: (evt) => {
        const item: AdminNotificationItem = {
            id: uid(),
            createdAt: Date.now(),
            read: false,
            eventType: evt.eventType,
            reservationId: evt.eventType === "BROWSER_ACCESS_PENDING" ? undefined : evt.reservationId,
            reservationType: evt.eventType === "NEW_RESERVATION" ? evt.reservationType : undefined,
            browserClientUserId: evt.eventType === "BROWSER_ACCESS_PENDING" ? evt.browserClientUserId : undefined,
            username: evt.eventType === "BROWSER_ACCESS_PENDING" ? undefined : evt.username,
            phoneNumber: evt.phoneNumber,
            title: buildTitle(evt),
        };

        // держим максимум 30 уведомлений
        const next = [item, ...get().items].slice(0, 30);

        set({
            items: next,
            unreadCount: next.filter((x) => !x.read).length,
        });
    },

    markRead: (id) => {
        const next = get().items.map((x) => (x.id === id ? { ...x, read: true } : x));
        set({ items: next, unreadCount: next.filter((x) => !x.read).length });
    },

    markAllRead: () => {
        const next = get().items.map((x) => ({ ...x, read: true }));
        set({ items: next, unreadCount: 0 });
    },

    clear: () => set({ items: [], unreadCount: 0 }),
}));
